import app from 'flarum/common/app';
import { extend, override } from 'flarum/common/extend';
import { getJwtUser } from '@speareducation/util/auth';
import { userHasAccess } from '../../util';
import icon from 'flarum/common/helpers/icon';
import Dropdown from 'flarum/common/components/Dropdown';
import ItemList from 'flarum/common/utils/ItemList';
import IndexPage from 'flarum/forum/components/IndexPage';

export default () => {
    /**
     * Certain users do not have access to Spear Talk because of their Organization. Show an error message.
     */
    if (getJwtUser()?.isTalkRestricted()) {
      app.alerts.show({ type: 'error' }, 'Spear Talk is not available to you. Please contact your DSO for more information.');
    }

    /**
     * Hide "Start Discussion" if not logged in
     */
    extend(IndexPage.prototype, 'sidebarItems', function (items) {
        items.get('newDiscussion').attrs.icon = 'fas fa-comment-dots';

        if (!app.session.user || !userHasAccess()) {
            items.remove('newDiscussion');
        }
    })

    /**
     * MOBILE-489 Clicking "New Discussion", then "x", then "New Discussion" again causes a browser prompt. Resolve this
     */
    override(IndexPage.prototype, 'newDiscussionAction', async function (original) {
        if (app.composer?.position !== 'hidden') {
            app.composer.show();
        } else {
            return await original();
        }
    });

    /**
     * Override Toolbar
     * @param {ItemList} origItems
     */
    override(IndexPage.prototype, 'viewItems', function (origItems) {
        const items = new ItemList();
        const sortMap = app.discussions.sortMap();

        const sortOptions = {
            latest: {
                label: 'Last Replied',
                description: 'Discussion most recently replied to',
            },
            newest: {
                label: 'Newest Discussion',
                description: 'Newest discussion started',
            },
            oldest: {
                label: 'Oldest Discussion',
                description: 'Oldest discussion started',
            },
            top: {
                label: 'Most Replies',
                description: 'Discussion with the most replies',
            },
            popular: {
                label: 'Most Viewed',
                description: 'Discussion that has the most views',
            },
            unpopular: {
                label: 'Least Viewed',
                description: 'Discussion that has the least views',
            },
        };

        // append any sort extensions to sort Options
        for (const sortKey in sortMap) {
            if (!sortOptions[sortKey]) {
                const label = app.translator.trans(`core.forum.index_sort.${sortKey}_button`);
                sortOptions[sortKey] = {
                    label: label,
                    description: label,
                }
            }
        }

        const selectedSortKey = app.search.params().sort || Object.keys(sortOptions)[0];
        const buttonLabel = sortOptions[selectedSortKey].label;

        // Get category tag title and description
        const slug = window.location.pathname.split('/').pop();
        const findTag = (slug) => app.store.all('tags').find((tag) => tag.slug().localeCompare(slug, undefined, { sensitivity: 'base' }) === 0);

        const categoryTitle = findTag(slug)?.name() ?? 'All Discussions';
        const categoryDescription = findTag(slug)?.description() 
            ?? 'Post your clinical cases and share feedback with your colleagues. Case acceptance dilemmas and technical questions can also be shared.';
        const categoryIcon = findTag(slug)?.icon() ?? 'fas fa-comments';

        items.add(
            'allDiscussionsToolbar',
            <>
                <div class='toolbar'>
                    <div class='toolbar-title'>
                        <i aria-hidden="true" class={`${categoryIcon}`}></i>
                        <h1>
                            {categoryTitle}
                        </h1>
                    </div>

                <div class='sort-container'>
                    <div class='item-sort'>
                        <Dropdown
                            buttonClassName="Button"
                            icon="fa fa-filter"
                            label={buttonLabel}
                            accessibleToggleLabel={buttonLabel}
                        >
                            {Object.entries(sortOptions).map(([sortKey, sortObj]) => {
                                const {label, description} = sortObj;
                                const active = sortKey === selectedSortKey;
                            
                                return (
                                    <button onclick={app.search.changeSort.bind(app.search, sortKey)} active={active}
                                            class="hasIcon">
                                        <div>
                                            <div class="dropdown--label">{label}</div>
                                            <small class="dropdown--description">{description}</small>
                                        </div>
                                        {active ? icon('fas fa-check', {className: 'Button-icon'}) : null}
                                    </button>
                                );
                            })}
                        </Dropdown>
                    </div>
                </div>
                </div>

                <p class="toolbar-text">
                    {categoryDescription}
                </p>
            </>
        );

        const buttonGroup = document.querySelector('.Dropdown-toggle');

        if (!app.session.user) {
            buttonGroup?.classList.add('sideNav-not-logged-in');
        }

        return items;
    });

    /**
     * Remove action items - ItemRefresh, MarkAllAsRead
     */
    override(IndexPage.prototype, 'actionItems', function (original) { 
        return new ItemList();
    });
}
